// Централизованный словарь переводов статусов сущностей юр.агента.
// Используется во всех карточках и таблицах вместо сырых английских значений.

const CONTRACT_STATUS: Record<string, string> = {
  draft: 'Черновик',
  // Асинхронный парсинг комплекта: upload отвечает сразу, doc-processor
  // извлекает текст в фоне (Job::ParseContract). Реальный анализ — после.
  parsing: 'Обработка документов',
  under_review: 'На проверке',
  analyzed: 'Проанализирован',
  // Stage G / sweeper-recovery: договор отложен на ручной разбор юристом.
  // Возникает при превышении sweep-retry лимита (poison-job) или ручной
  // пометке зомби-записи. Анализ не запущен, нужно решение юриста.
  manual_review: 'Ручная проверка',
  // Парсинг комплекта не удался (битый файл / скан без текстового слоя) —
  // юристу нужно перезагрузить документ.
  parse_failed: 'Не удалось обработать',
  // Анализ остановлен вручную («Остановить анализ») — юрист может запустить
  // заново или догрузить документы.
  cancelled: 'Анализ остановлен',
  failed_analysis: 'Анализ не удался',
  // Преддоговор заключён: в реестре «Договоры» создана отдельная карточка для
  // подписанного скана. Сам преддоговор остаётся архивом проработки.
  concluded: 'Заключён',
  signed: 'Подписан',
  expired: 'Истёк',
  terminated: 'Расторгнут',
  archived: 'В архиве',
};

const DEADLINE_STATUS: Record<string, string> = {
  pending: 'Ожидает',
  notified: 'Уведомлён',
  overdue: 'Просрочен',
  closed: 'Выполнен',
};

const CORRESPONDENCE_STATUS: Record<string, string> = {
  received: 'Получено',
  classified: 'Классифицировано',
  in_review: 'На рассмотрении',
  draft_ready: 'Проект готов',
  sent: 'Отправлено',
  returned: 'Возвращено диспетчеру',
  closed: 'Закрыто',
};

// Жизненный цикл претензии/иска. Набор общий для incoming и outgoing —
// лейблы нейтральные, направление видно из карточки.
const CLAIM_STATUS: Record<string, string> = {
  new: 'Новая',
  in_review: 'На изучении',
  response_drafted: 'Готов проект ответа',
  sent: 'Отправлена',
  awaiting_reply: 'Ждём ответ',
  satisfied: 'Удовлетворена',
  rejected: 'Отказано',
  litigation: 'Передано в суд',
  closed: 'Закрыта',
};

/** Упорядоченный список стадий — для Select и фильтров. */
export const CLAIM_STAGES: { value: string; label: string }[] = [
  { value: 'new', label: 'Новая' },
  { value: 'in_review', label: 'На изучении' },
  { value: 'response_drafted', label: 'Готов проект ответа' },
  { value: 'sent', label: 'Отправлена' },
  { value: 'awaiting_reply', label: 'Ждём ответ' },
  { value: 'satisfied', label: 'Удовлетворена' },
  { value: 'rejected', label: 'Отказано' },
  { value: 'litigation', label: 'Передано в суд' },
  { value: 'closed', label: 'Закрыта' },
];

const COMPLIANCE_STATUS: Record<string, string> = {
  pending: 'Ожидает',
  processing: 'Анализируется',
  completed: 'Готово',
  failed: 'Ошибка',
};

const KNOWLEDGE_STATUS: Record<string, string> = {
  active: 'Активен',
  processing: 'Обрабатывается',
  archived: 'В архиве',
  failed: 'Ошибка',
};

const CORRESPONDENCE_SOURCE: Record<string, string> = {
  dispatcher: 'От диспетчера',
  internal: 'Внутреннее',
  manual: 'Ручной ввод',
};

const CONTRACT_TYPE: Record<string, string> = {
  works: 'Подряд',
  supply: 'Поставка',
  purchase: 'Купля-продажа',
  services: 'Услуги',
  lease: 'Аренда',
  agency: 'Агентский',
  other: 'Иной',
};

export const CONTRACT_TYPE_OPTIONS = [
  { value: 'works', label: 'Подряд (строительство)' },
  { value: 'supply', label: 'Поставка' },
  { value: 'purchase', label: 'Купля-продажа' },
  { value: 'services', label: 'Услуги' },
  { value: 'lease', label: 'Аренда' },
  { value: 'agency', label: 'Агентский' },
  { value: 'other', label: 'Иной' },
];

export function contractTypeLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return CONTRACT_TYPE[s] ?? s;
}

const COUNTERPARTY_STATUS: Record<string, string> = {
  ACTIVE: 'Действующий',
  LIQUIDATING: 'Ликвидируется',
  LIQUIDATED: 'Ликвидирован',
  REORGANIZING: 'Реорганизуется',
  BANKRUPT: 'Банкрот',
  UNKNOWN: 'Не проверялся',
};

export function counterpartyStatusLabel(s: string | null | undefined): string {
  if (!s) return 'Не проверялся';
  return COUNTERPARTY_STATUS[s] ?? s;
}

/** Безопасный «индикатор риска» статуса — для подсветки в UI. */
export function counterpartyStatusTone(
  s: string | null | undefined,
): 'success' | 'danger' | 'warning' | 'neutral' {
  switch (s) {
    case 'ACTIVE':
      return 'success';
    case 'LIQUIDATED':
    case 'BANKRUPT':
      return 'danger';
    case 'LIQUIDATING':
    case 'REORGANIZING':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function contractStatusLabel(s: string): string {
  return CONTRACT_STATUS[s] ?? s;
}

export function deadlineStatusLabel(s: string): string {
  return DEADLINE_STATUS[s] ?? s;
}

/** Госорган запроса — коды классификатора писем (как на странице «Госорганы»).
 *  На главной Юриста код шёл как есть: «OTHER» под каждым запросом. */
const GOV_AUTHORITY: Record<string, string> = {
  FNS: 'ФНС',
  PROSECUTOR: 'Прокуратура',
  GOSSTROYNADZOR: 'Госстройнадзор',
  ROSTECHNADZOR: 'Ростехнадзор',
  CUSTOMS: 'Таможня',
  MVD: 'МВД',
  FAS: 'ФАС',
  ROSPOTREBNADZOR: 'Роспотребнадзор',
  OTHER: 'Иной орган',
};

export function govAuthorityLabel(code: string | null | undefined): string {
  if (!code) return 'Орган не указан';
  return GOV_AUTHORITY[code] ?? code;
}

export function correspondenceStatusLabel(s: string): string {
  return CORRESPONDENCE_STATUS[s] ?? s;
}

export function correspondenceSourceLabel(s: string): string {
  return CORRESPONDENCE_SOURCE[s] ?? s;
}

export function claimStatusLabel(s: string): string {
  return CLAIM_STATUS[s] ?? s;
}

// Жизненный цикл иска (новая сущность lawsuit, отдельный pipeline ADR-0001).
// Не путать с CLAIM_STATUS — там стадии переписки по претензии/иску как объекту
// делопроизводства; здесь — статусы async-сборки DOCX искового заявления.
const LAWSUIT_STATUS: Record<string, string> = {
  draft: 'Черновик',
  composing: 'Составляется',
  ready: 'Готов',
  failed: 'Ошибка',
};

export function lawsuitStatusLabel(s: string): string {
  return LAWSUIT_STATUS[s] ?? s;
}

export function lawsuitStatusTone(
  s: string,
): 'success' | 'danger' | 'warning' | 'info' | 'neutral' {
  switch (s) {
    case 'ready':
      return 'success';
    case 'failed':
      return 'danger';
    case 'composing':
      return 'info';
    case 'draft':
    default:
      return 'neutral';
  }
}

export function complianceStatusLabel(s: string): string {
  return COMPLIANCE_STATUS[s] ?? s;
}

export function knowledgeStatusLabel(s: string): string {
  return KNOWLEDGE_STATUS[s] ?? s;
}
